import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import EmployeeLink from '../components/EmployeeLink';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import api from '../lib/api';
import { fmtHours } from '../lib/formatters';
import { Download, Clock, Activity, Trophy, Calendar, Loader2 } from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function heatmapCellColor(avg) {
  if (avg == null) return '#f9fafb';
  if (avg >= 80) return '#1d4ed8';
  if (avg >= 60) return '#3b82f6';
  if (avg >= 40) return '#93c5fd';
  if (avg >= 20) return '#dbeafe';
  if (avg > 0)  return '#eff6ff';
  return '#f9fafb';
}

function PeakHoursHeatmap({ userId, from, to }) {
  const { data, isLoading } = useQuery({
    queryKey: ['peak-hours', userId, from, to],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (userId) params.set('userId', userId);
      const res = await api.get(`/api/reports/peak-hours?${params}`);
      return res.data?.data;
    },
  });

  // Build lookup: matrix[dow][hour] = avgActivity
  const lookup = useMemo(() => {
    const m = {};
    for (const row of data?.matrix || []) {
      if (!m[row.dow]) m[row.dow] = {};
      m[row.dow][row.hour] = row.avgActivity;
    }
    return m;
  }, [data]);

  const hasData = (data?.matrix || []).length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-900">Peak Productivity Hours</h2>
        {data?.peakHour != null && (
          <span className="text-xs text-gray-500">
            Peak: {DOW_LABELS[data.peakDow]} {data.peakHour}:00–{data.peakHour + 1}:00
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
        </div>
      ) : !hasData ? (
        <div className="flex items-center justify-center h-40 text-sm text-gray-400">
          No activity data for selected range.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr>
                <th className="w-8 text-right pr-2 text-gray-400 font-normal pb-1" />
                {Array.from({ length: 24 }, (_, h) => (
                  <th
                    key={h}
                    className="text-center font-normal text-gray-400 pb-1"
                    style={{ minWidth: 20 }}
                  >
                    {h % 3 === 0 ? `${h}h` : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 0].map((dow) => (
                <tr key={dow}>
                  <td className="text-right pr-2 text-gray-500 font-medium py-0.5 whitespace-nowrap">
                    {DOW_LABELS[dow]}
                  </td>
                  {Array.from({ length: 24 }, (_, h) => {
                    const avg = lookup[dow]?.[h];
                    return (
                      <td
                        key={h}
                        className="p-0"
                        title={avg != null ? `${DOW_LABELS[dow]} ${h}:00 — ${Math.round(avg)}% avg activity` : undefined}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: 18,
                            background: heatmapCellColor(avg),
                            borderRadius: 3,
                            margin: '1px',
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[10px] text-gray-400">Low</span>
            {['#eff6ff', '#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8'].map((c) => (
              <div key={c} style={{ width: 16, height: 10, background: c, borderRadius: 2 }} />
            ))}
            <span className="text-[10px] text-gray-400">High activity</span>
          </div>
        </div>
      )}
    </div>
  );
}

function getPresetDates(preset) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (preset === 'today') {
    const s = fmt(now);
    return { dateFrom: s, dateTo: s };
  }
  if (preset === 'week') {
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((day + 6) % 7));
    return { dateFrom: fmt(mon), dateTo: fmt(now) };
  }
  if (preset === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateFrom: fmt(first), dateTo: fmt(now) };
  }
  return { dateFrom: '', dateTo: '' };
}

function StatCard({ icon: Icon, label, value, sub, loading, color = 'blue' }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${colorMap[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-24 bg-gray-200 rounded animate-pulse" />
        ) : (
          <p className="mt-0.5 text-2xl font-bold text-gray-900">{value}</p>
        )}
        {sub && !loading && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function Reports() {
  const [preset, setPreset] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');

  const dates = useMemo(() => {
    if (preset === 'custom') return { dateFrom: customFrom, dateTo: customTo };
    return getPresetDates(preset);
  }, [preset, customFrom, customTo]);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (dates.dateFrom) p.set('dateFrom', dates.dateFrom);
    if (dates.dateTo) p.set('dateTo', dates.dateTo);
    if (userId) p.set('userId', userId);
    if (projectId) p.set('projectId', projectId);
    return p.toString();
  }, [dates, userId, projectId]);

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const res = await api.get('/api/projects');
      return res.data?.data || [];
    },
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports', queryParams],
    queryFn: async () => {
      const res = await api.get(`/api/reports/summary?${queryParams}`);
      return res.data?.data || {};
    },
    enabled: !!(dates.dateFrom || dates.dateTo || preset !== 'custom'),
  });

  const summary = data || {};
  const hoursPerUserPerDay = data?.hoursPerUserPerDay || [];
  const activityOverTime = data?.activityOverTime || [];
  const userKeys = useMemo(
    () => [...new Set(hoursPerUserPerDay.flatMap((d) => Object.keys(d).filter((k) => k !== 'date')))],
    [hoursPerUserPerDay]
  );

  const handleExport = (format) => {
    const params = new URLSearchParams(queryParams);
    params.set('format', format);
    const url = `${import.meta.env.VITE_API_URL || 'https://api.haydevs.com'}/api/reports/export?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${dates.dateFrom || 'all'}.${format}`;
    a.click();
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load reports.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Preset */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            {['today', 'week', 'month', 'custom'].map((p) => (
              <button
                key={p}
                onClick={() => setPreset(p)}
                className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                  preset === p ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : p === 'today' ? 'Today' : 'Custom'}
              </button>
            ))}
          </div>
        </div>

        {/* Custom dates */}
        {preset === 'custom' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </>
        )}

        {/* User filter */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Users</option>
            {(usersData || []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>

        {/* Project filter */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Projects</option>
            {(projectsData || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Export buttons */}
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={Clock}
          label="Total Hours"
          value={fmtHours(parseFloat(summary.totalHours) || 0)}
          sub="in selected range"
          loading={isLoading}
          color="blue"
        />
        <StatCard
          icon={Activity}
          label="Avg Activity"
          value={`${Math.round(summary.avgActivity || 0)}%`}
          sub="keyboard + mouse"
          loading={isLoading}
          color="green"
        />
        <StatCard
          icon={Trophy}
          label="Most Active Member"
          value={
            summary.mostActiveMember ? (
              <EmployeeLink
                userId={summary.mostActiveMember.user?.id}
                name={summary.mostActiveMember.user?.name || '--'}
                className="text-2xl font-bold text-gray-900"
              />
            ) : '--'
          }
          sub={summary.mostActiveMember ? fmtHours(parseFloat(summary.mostActiveMember.totalHours) || 0) : 'no data'}
          loading={isLoading}
          color="purple"
        />
      </div>

      {/* Hours per user per day */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Hours Per User Per Day</h2>
        {isLoading ? (
          <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
        ) : hoursPerUserPerDay.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-sm text-gray-400">
            No data for selected range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hoursPerUserPerDay} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                unit="h"
              />
              <Tooltip
                formatter={(val, name) => [fmtHours(val), name]}
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {userKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={COLORS[i % COLORS.length]}
                  radius={i === userKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Peak hours heatmap */}
      <PeakHoursHeatmap userId={userId} from={dates.dateFrom} to={dates.dateTo} />

      {/* Activity over time */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Activity Level Over Time</h2>
        {isLoading ? (
          <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
        ) : activityOverTime.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-sm text-gray-400">
            No activity data for selected range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={activityOverTime} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                unit="%"
                domain={[0, 100]}
              />
              <Tooltip
                formatter={(val) => [`${Math.round(val)}%`, 'Activity']}
                contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {[...new Set(activityOverTime.flatMap((d) => Object.keys(d).filter((k) => k !== 'date')))].map(
                (key, i) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
